import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as styles from './Wizard.module.css';

/**
 * CheckIcon - Simple checkmark icon for completed steps
 */
function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M13.3334 4L6.00002 11.3333L2.66669 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * ArrowRightIcon - Arrow icon for navigation buttons
 */
function ArrowRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M6 12L10 8L6 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Wizard - A multi-step form/wizard component with step navigation
 * 
 * @example
 * // Simple usage
 * <Wizard
 *   title="Create Report"
 *   steps={[
 *     { id: 1, title: 'Basic Info', component: Step1 },
 *     { id: 2, title: 'Configure', component: Step2 }
 *   ]}
 *   onClose={() => console.log('closed')}
 * />
 * 
 * @example
 * // With controlled step
 * const [step, setStep] = useState(0);
 * <Wizard
 *   steps={steps}
 *   currentStep={step}
 *   onStepChange={setStep}
 *   onComplete={(data) => console.log('Done!', data)}
 * />
 * 
 * @param {Object} props
 * @param {Array} props.steps - Array of step objects {id, title, component}
 * @param {number} props.currentStep - Currently active step index (0-based, optional - will be controlled internally if not provided)
 * @param {Function} props.onStepChange - Callback when step changes (optional)
 * @param {string} props.title - Main wizard title
 * @param {string} props.subtitle - Wizard subtitle (optional)
 * @param {Function} props.onClose - Close/cancel handler (optional)
 * @param {Function} props.onComplete - Called when wizard is completed (optional)
 * @param {React.ReactNode} props.children - Alternative to steps array (for custom layout)
 * @param {Object} props.stepData - Data passed to all step components (optional)
 * @param {string} props.closeButtonText - Text for close button (default: "Close")
 * @param {boolean} props.showSidebar - Whether to show step sidebar (default: true)
 * @param {boolean} props.fullScreen - Whether wizard is full screen (default: false)
 * @param {React.ReactNode} props.footer - Custom footer content (optional)
 * @param {string} props.className - Additional CSS class (optional)
 */
function Wizard({
  steps,
  currentStep,
  onStepChange,
  title,
  subtitle,
  onClose,
  onComplete,
  onNext,
  onBack,
  children,
  stepData,
  closeButtonText,
  completeButtonText,
  nextButtonText,
  nextLoadingText,
  backButtonText,
  showSidebar,
  fullScreen,
  footer,
  className,
}) {
  const [localStep, setLocalStep] = useState(0);
  const [isNextLoading, setIsNextLoading] = useState(false);

  // Use controlled or uncontrolled step
  const activeStep = currentStep !== undefined ? currentStep : localStep;
  
  const handleStepChange = useCallback((newStep) => {
    if (onStepChange) {
      onStepChange(newStep);
    } else {
      setLocalStep(newStep);
    }
  }, [onStepChange]);

  // Navigation handlers — supports both sync and async onNext callbacks.
  // Sync: return false to block, anything else to proceed.
  // Async: resolve false to block; shows loading state on the Next button while awaiting.
  const handleNext = useCallback(async () => {
    if (onNext) {
      const result = onNext(activeStep);
      if (result === false) {
        return;
      }
      if (result && typeof result.then === 'function') {
        setIsNextLoading(true);
        try {
          const asyncResult = await result;
          if (asyncResult === false) {
            return;
          }
        } finally {
          setIsNextLoading(false);
        }
      }
    }
    
    if (steps && activeStep < steps.length - 1) {
      handleStepChange(activeStep + 1);
    }
  }, [activeStep, steps, handleStepChange, onNext]);

  const handleBackClick = useCallback(() => {
    // If custom onBack is provided, call it
    if (onBack) {
      const result = onBack(activeStep);
      // If onBack returns false, don't proceed
      if (result === false) {
        return;
      }
    }
    
    if (activeStep > 0) {
      handleStepChange(activeStep - 1);
    }
  }, [activeStep, handleStepChange, onBack]);

  const handleComplete = useCallback(() => {
    if (onComplete) {
      onComplete();
    }
  }, [onComplete]);

  const CurrentStepComponent = useMemo(() => {
    if (children) return null;
    if (!steps || steps.length === 0) return null;
    return steps[activeStep]?.component;
  }, [steps, activeStep, children]);

  const containerClasses = [
    styles.wizardContainer,
    fullScreen && styles.fullScreen,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} role="dialog" aria-labelledby="wizard-title">
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div>
            {title && <h1 id="wizard-title" className={styles.title}>{title}</h1>}
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className={styles.mainContainer}>
        {/* Sidebar with Steps */}
        {showSidebar !== false && steps && steps.length > 0 && (
          <nav className={styles.sidebar} aria-label="Wizard steps">
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              
              return (
                <div
                  key={step.id || index}
                  className={`${styles.stepItem} ${
                    isActive ? styles.active : ''
                  } ${isCompleted ? styles.completed : ''}`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <div className={styles.stepNumber} aria-hidden="true">
                    {isCompleted ? <CheckIcon /> : index + 1}
                  </div>
                  <span className={styles.stepName}>{step.title}</span>
                </div>
              );
            })}
          </nav>
        )}

        {/* Content Area */}
        <main className={styles.contentArea} role="main">
          <div className={styles.contentWrapper}>
            <div className={styles.contentCard}>
              {CurrentStepComponent ? (
                <CurrentStepComponent
                  {...stepData}
                  currentStep={activeStep}
                  onNext={() => handleStepChange(activeStep + 1)}
                  onBack={() => handleStepChange(activeStep - 1)}
                  onComplete={onComplete}
                  isFirstStep={activeStep === 0}
                  isLastStep={activeStep === steps.length - 1}
                />
              ) : (
                children
              )}
            </div>
          </div>

          {/* Footer with Navigation */}
          <div className={styles.footer}>
            {/* Custom footer content (e.g., Show Preview button) */}
            {footer && <div className={styles.customFooter}>{footer}</div>}
            
            {/* Built-in Navigation Buttons */}
            {steps && steps.length > 0 && (
              <div className={styles.navigationFooter}>
                {/* All buttons on the right: Close Page, Back, Next/Complete */}
                <div className={styles.footerRight}>
                  {onClose && (
                    <button
                      type="button"
                      className={styles.closeButton}
                      onClick={onClose}
                      aria-label={closeButtonText || 'Close wizard'}
                    >
                      {closeButtonText}
                    </button>
                  )}

                  {activeStep > 0 && (
                    <button
                      type="button"
                      className={styles.backButton}
                      onClick={handleBackClick}
                    >
                      <ArrowRightIcon />
                      {backButtonText || 'Back'}
                    </button>
                  )}

                  {activeStep < steps.length - 1 ? (
                    <button
                      type="button"
                      className={`${styles.nextButton} ${isNextLoading ? styles.nextButtonLoading : ''}`}
                      onClick={isNextLoading ? undefined : handleNext}
                      aria-disabled={isNextLoading}
                      aria-busy={isNextLoading}
                    >
                      {isNextLoading ? (nextLoadingText || 'Loading...') : (nextButtonText || 'Next')}
                      {!isNextLoading && <ArrowRightIcon />}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={styles.completeButton}
                      onClick={handleComplete}
                    >
                      {completeButtonText || 'Complete'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

Wizard.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      component: PropTypes.elementType,
    })
  ),
  currentStep: PropTypes.number,
  onStepChange: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onClose: PropTypes.func,
  onComplete: PropTypes.func,
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  children: PropTypes.node,
  stepData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  closeButtonText: PropTypes.string,
  completeButtonText: PropTypes.string,
  nextButtonText: PropTypes.string,
  nextLoadingText: PropTypes.string,
  backButtonText: PropTypes.string,
  showSidebar: PropTypes.bool,
  fullScreen: PropTypes.bool,
  footer: PropTypes.node,
  className: PropTypes.string,
};

Wizard.defaultProps = {
  steps: [],
  currentStep: undefined,
  onStepChange: null,
  title: '',
  subtitle: '',
  onClose: null,
  onComplete: null,
  onNext: null,
  onBack: null,
  children: null,
  stepData: {},
  closeButtonText: 'Close',
  completeButtonText: 'Complete',
  nextButtonText: 'Next',
  nextLoadingText: 'Loading...',
  backButtonText: 'Back',
  showSidebar: true,
  fullScreen: false,
  footer: null,
  className: '',
};

export default Wizard;
